import ClientLogger from "../../../logging/ClientLogger";
import { Location } from "../../../../models";
import LocationDAO from "../LocationDAO";
import LocationMetricNames from "../../LocationMetricNames";
import ResourceNotFoundError from "../../../ResourceNotFoundError";
import _ from "lodash";

export default class MockDataStoreLocationDAO implements LocationDAO {
    private static readonly STORAGE_NAME = "mock-locations";
    private readonly logger: ClientLogger;

    constructor(
        logger: ClientLogger
    ) {
        this.logger = logger;
    }

    private loadMock(): Location[] {
        let mock = new Array<Location>();
        const mockStr = localStorage.getItem(MockDataStoreLocationDAO.STORAGE_NAME);

        if (mockStr) {
            mock = JSON.parse(mockStr) as Location[];
        }

        return mock;
    }

    private saveMock(mock: Location[]): void {
        localStorage.setItem(MockDataStoreLocationDAO.STORAGE_NAME, JSON.stringify(mock));
    }

    public async getLocationById(locationId: string): Promise<Location> {
        const mock = this.loadMock();
        const result = mock.find((location): boolean => {
            return location.id === locationId;
        });
        
        if (!result) {
            throw new ResourceNotFoundError("Location");
        }
        return result;
    }

    public async getLocationsByPropertyId(propertyId: string): Promise<Array<Location>> {
        const mock = this.loadMock();
        return mock.filter((location): boolean => {
            return location.propertyId === propertyId;
        });
    }

    public async getLocationCountByParentId(parentId: string): Promise<number> {
        const locations: Array<Location> = await this.getLocationsByPropertyId(parentId);
        return locations.length;
    }

    public async editLocation(
        locationId: string,
        updatedLocation: Location
    ): Promise<Location> {
        try {
            const existingLocation: Location = await this.getLocationById(locationId);
            let result: Location = {
                ...existingLocation,
                propertyId: updatedLocation.propertyId,
                name: updatedLocation.name,
                localLastUpdatedTime: Date.now()
            };

            if (!_.isEqual(existingLocation, result)) {
                const mock = this.loadMock();
                const index = mock.findIndex((value): boolean => {
                    return value.id === result.id;
                });

                if (index >= 0) {
                    mock[index] = result;
                    this.saveMock(mock);
                }
            } else {
                result = existingLocation;
            }
            
            return result;
        } catch (error) {
            this.logger.warn(
                "Failed to update the location",
                error,
                ["LocationUpdateFailure"]
            );
            throw new Error("Failed to update the location");
        }
    }

    public async create(formState: Location): Promise<Location> {
        try {
            const locationToCreate: Location = new Location({
                ...formState,
                localCreationTime: Date.now(),
                localLastUpdatedTime: Date.now()
            });
            
            const mock = this.loadMock();
            const exists = mock.find((value): boolean => {
                return value.id === locationToCreate.id;
            });

            if (exists) {
                throw Error("Location already exists");
            }

            mock.push(locationToCreate);
            this.saveMock(mock);

            return locationToCreate;
        } catch (error) {
            this.logger.warn(
                "Failed to create the location",
                error,
                [LocationMetricNames.CREATE_FAILURE]
            );
            throw new Error("Failed to create the location");
        }
    }

    public async deleteLocation(locationId: string): Promise<void> {
        try {
            this.logger.info(
                `Deleting location with id: ${locationId}`,
                undefined,
                [LocationMetricNames.DELETE_ATTEMPT]
            );
            
            const mock = this.loadMock();
            const filteredMock = mock.filter((location): boolean => {
                return location.id !== locationId;
            });
            
            this.saveMock(filteredMock);
        } catch (error) {
            this.logger.error(
                `Failed to delete location with id: ${locationId}`,
                error,
                [LocationMetricNames.DELETE_FAILURE]
            );
            throw error;
        }
    }
} 